import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import api, { resolveMediaUrl } from '../lib/api';
import { triggerExitIntent } from '../lib/exitIntent';
import { ArrowLeft, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import SuggestedChanges from '../components/SuggestedChanges';
import AudioPlayer from '../components/AudioPlayer';
import TeacherRemark from '../components/review/TeacherRemark';
import { FeedbackPrompt } from '../components/feedback/FeedbackPrompt';
import { useAuth } from '../contexts/AuthContext';

const S = {
  hdrBg: '#dfe3e8', hdrBorder: '#c5c9ce', hdrText: '#2c3e50',
  blue: '#0b5cad', timerRed: '#c0392b', white: '#fff',
  textDark: '#2c3e50', textBody: '#34495e', textLight: '#7f8c8d',
  font: "'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
};

const SKILL_LABELS = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };

// A writing/speaking item is still being scored while it has neither a score nor feedback.
const hasPendingAi = (data) => {
  const ts = data?.target_skill || null;
  return (data?.detailed_responses || []).some(d => {
    if (d.response_type === 'text_response' && (ts === null || ts === 'writing')) return !d.ai_score && !d.ai_feedback;
    if (d.response_type === 'audio_response' && (ts === null || ts === 'speaking')) return !d.ai_score && !d.ai_feedback;
    return false;
  });
};

export default function ExamResults() {
  const { examId, attemptId } = useParams();
  const { user } = useAuth();
  const isPro = !!user && ((user.subscription_status && user.subscription_status !== 'free') || ['admin', 'teacher', 'contributor', 'sales'].includes(user.role));
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAI, setProcessingAI] = useState(false);
  const [processingLabel, setProcessingLabel] = useState('');
  const [waitedLong, setWaitedLong] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  // Step 4: surface demo-mode banner when Azure OpenAI keys are missing
  useEffect(() => {
    api.get('/api/health').then(r => {
      const azure = r?.data?.services?.azure_openai;
      setDemoMode(azure === false);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/api/exams/${examId}/results/${attemptId}`).then(r => {
      setResults(r.data);
      const ts = r.data.target_skill || null;
      const resps = r.data.detailed_responses || [];
      // Only check pending for skills that were actually tested
      const pendingW = ts === null || ts === 'writing' ? resps.filter(d => d.response_type === 'text_response' && !d.ai_score && !d.ai_feedback) : [];
      const pendingS = ts === null || ts === 'speaking' ? resps.filter(d => d.response_type === 'audio_response' && !d.ai_score && !d.ai_feedback) : [];
      if (pendingW.length > 0 || pendingS.length > 0) {
        setProcessingAI(true);
        const labels = [];
        if (pendingW.length > 0) labels.push('Writing');
        if (pendingS.length > 0) labels.push('Speaking');
        setProcessingLabel(labels.join(' & '));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [examId, attemptId]);

  // Arriving from a teacher-review notification (bell / dashboard / e-mail): open on the teacher's remarks.
  const fromReview = searchParams.get('from') === 'review';
  useEffect(() => {
    if (loading || !fromReview) return;
    const t = setTimeout(() => document.getElementById('teacher-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
    return () => clearTimeout(t);
  }, [loading, fromReview]);

  // Just finished the test (ExamTake passes state.finished) → exit-intent offer 3 s after the scores load; the flag is cleared so a reload doesn't repeat it.
  const location = useLocation();
  const navigate = useNavigate();
  const justFinished = !!location.state?.finished;
  useEffect(() => {
    if (loading || !results || !justFinished) return;
    navigate(location.pathname + location.search, { replace: true, state: null });
    triggerExitIntent('test_done', 3000);
  }, [loading, results, justFinished, navigate, location.pathname, location.search]);

  // Poll for AI scores if processing — one cycle at a time: /score-pending (which
  // may re-run speech-to-text from the saved audio) → fresh results → wait → again.
  useEffect(() => {
    if (!processingAI) return undefined;
    let stop = false;
    let timer = null;
    const started = Date.now();
    const tick = async () => {
      try { await api.post(`/api/exams/${examId}/score-pending/${attemptId}`); } catch {}
      if (stop) return;
      try {
        const r = await api.get(`/api/exams/${examId}/results/${attemptId}`);
        if (stop) return;
        setResults(r.data);
        if (!hasPendingAi(r.data)) { setProcessingAI(false); return; }
      } catch {}
      setWaitedLong(Date.now() - started > 120000);
      timer = setTimeout(tick, 7000);
    };
    timer = setTimeout(tick, 0);
    return () => { stop = true; clearTimeout(timer); };
  }, [processingAI, examId, attemptId]);

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontFamily: S.font }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #ddd', borderTop: `4px solid ${S.blue}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#666' }}>Loading results...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!results) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: S.font }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: '16px', color: '#999', marginBottom: '16px' }}>Results not found</p>
        <Link to="/exams" style={{ color: S.blue, textDecoration: 'none', fontWeight: 'bold' }}>Back to Exams</Link>
      </div>
    </div>
  );

  const examName = results.exam?.name || 'Exam';
  const testNum = results.exam?.exam_number || 1;
  const answerKey = results.answer_key || [];
  const allResps = results.detailed_responses || [];
  const targetSkill = results.target_skill || null; // null = full exam
  const teacherCommentCount = allResps.filter((r) => r.teacher_comment && r.teacher_comment.text).length;
  // question_id → published teacher remark (Listening/Reading remarks render inside the Answer Key rows)
  const remarkByQid = Object.fromEntries(allResps.filter((r) => r.teacher_comment?.text && r.question_id).map((r) => [r.question_id, r.teacher_comment]));

  // Display skills based on target_skill
  const allSkillsInAK = [...new Set(answerKey.map(s => s.skill_id))];
  const displaySkills = targetSkill ? [targetSkill] : allSkillsInAK;

  // Filter answer key to display skills and only MCQ / dropdown_fill / dropdown_paragraph sections
  const filteredAK = answerKey.filter(s =>
    displaySkills.includes(s.skill_id) &&
    s.questions.some(q => q.question_type === 'mcq' || q.question_type === 'dropdown_fill' || q.question_type === 'dropdown_paragraph')
  );

  // Flatten a section's scored questions into a flat row list for the Answer
  // Key table. dropdown_paragraph questions explode into one row per statement
  // so the numbering and per-statement correctness matches what the student
  // actually saw during the exam (iter56d switched R1/L4 to dropdown_paragraph;
  // before this fix those rows were silently skipped, showing an empty AK and
  // a 0 score).
  const flattenRows = (questions) => {
    const rows = [];
    let counter = 0;
    for (const q of questions || []) {
      if (q.question_type === 'mcq' || q.question_type === 'dropdown_fill') {
        counter += 1;
        rows.push({
          key: q.id,
          qid: q.id,
          index: counter,
          correctText: q.correct_text || '—',
          userText: q.user_answer || '',
          isCorrect: !!q.is_correct,
          hasUserAnswer: !!q.user_answer,
          remark: remarkByQid[q.id] || null,
        });
      } else if (q.question_type === 'dropdown_paragraph' && Array.isArray(q.statement_results)) {
        q.statement_results.forEach((s, i) => {
          counter += 1;
          rows.push({
            key: `${q.id}-${s.statement_id || counter}`,
            qid: q.id,
            index: counter,
            correctText: s.correct_option_text || s.correct_option_id || '—',
            userText: s.selected_option_text || '',
            isCorrect: !!s.is_correct,
            hasUserAnswer: !!s.selected_option_id,
            remark: i === q.statement_results.length - 1 ? remarkByQid[q.id] || null : null,
          });
        });
      }
    }
    return rows;
  };

  // Calculate scores per skill (MCQ/dropdown_fill count as 1 each;
  // dropdown_paragraph contributes per-statement — matching the backend's
  // listening_total / reading_total tallies).
  const skillScores = {};
  displaySkills.forEach(sk => {
    const sections = filteredAK.filter(s => s.skill_id === sk);
    let correct = 0, total = 0;
    sections.forEach(sec => {
      sec.questions.forEach(q => {
        if (q.question_type === 'mcq' || q.question_type === 'dropdown_fill') {
          total += 1;
          if (q.is_correct) correct += 1;
        } else if (q.question_type === 'dropdown_paragraph' && Array.isArray(q.statement_results)) {
          total += q.statement_results.length;
          correct += q.statement_results.filter(s => s.is_correct).length;
        }
      });
    });
    skillScores[sk] = { correct, total, clb: results[`${sk}_score`] || 0 };
  });

  const writingResps = displaySkills.includes('writing') ? allResps.filter(r => r.response_type === 'text_response') : [];
  const speakingResps = displaySkills.includes('speaking') ? allResps.filter(r => r.response_type === 'audio_response') : [];

  // iter63 — Derive the real CELPIP task number from `part_id` (e.g. "S1" -> 1,
  // "W2" -> 2) so the Score Summary labels "Speaking Task N" / "Writing Task N"
  // match what the student actually recorded. Before this fix, labels came from
  // the array-index order which depends on DB insertion order and could show
  // S1 as "Speaking Task 5". Falls back to array index for legacy attempts
  // missing `part_id`.
  const taskNumberFromPart = (partId) => {
    if (!partId || typeof partId !== 'string') return null;
    const m = partId.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  };
  const sortByTaskNumber = (arr, fallbackPrefix) => {
    return [...arr]
      .map((r, idx) => ({ r, idx, n: taskNumberFromPart(r.part_id) }))
      .sort((a, b) => {
        if (a.n != null && b.n != null) return a.n - b.n;
        if (a.n != null) return -1;
        if (b.n != null) return 1;
        return a.idx - b.idx;
      })
      .map((row, i) => ({
        r: row.r,
        label: `${fallbackPrefix} Task ${row.n != null ? row.n : i + 1}`,
      }));
  };
  const sortedWriting = sortByTaskNumber(writingResps, 'Writing');
  const sortedSpeaking = sortByTaskNumber(speakingResps, 'Speaking');

  // iter63 — Combined CLB for Speaking and Writing across all tasks (avg of
  // each task's overall_clb, rounded to 0.5). Backend already computes this
  // on the attempt doc as `speaking_score` / `writing_score`; we just surface
  // it here so the student sees their overall section CLB at a glance.
  const computeCombinedClb = (resps) => {
    const scores = resps
      .map(r => {
        const fb = r.ai_feedback || r.ai_score;
        const s = (fb && typeof fb === 'object') ? fb.overall_clb : (typeof r.ai_score === 'number' ? r.ai_score : 0);
        return typeof s === 'number' ? s : 0;
      })
      .filter(s => s > 0);
    if (scores.length === 0) return 0;
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Math.round(avg * 2) / 2;
  };
  const combinedSpeakingClb = computeCombinedClb(speakingResps);
  const combinedWritingClb = computeCombinedClb(writingResps);

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: S.font }}>
      {/* Header */}
      <div style={{ background: '#1a1f2e', padding: '6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/exams" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/logo-navbar-dark.png" alt="CELPIPMax" style={{ width: '22px', height: '22px' }} />
          <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>CELPIPMax</span>
        </Link>
        <Link to="/exams" style={{ color: '#94a3b8', fontSize: '12px', textDecoration: 'none' }}>Back to Exams</Link>
      </div>

      {/* Step 4: AI scoring demo-mode banner — visible only when Azure OpenAI keys are missing */}
      {demoMode && (
        <div
          data-testid="demo-mode-banner"
          style={{
            background: '#fef3c7',
            borderBottom: '1px solid #f59e0b',
            color: '#78350f',
            padding: '10px 24px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontFamily: S.font,
          }}
        >
          <span style={{ fontSize: '16px' }} aria-hidden="true">⚠</span>
          <span>
            <strong>AI scoring is in demo mode.</strong> Writing & speaking scores are illustrative only — Azure OpenAI keys are not configured on this server.
          </span>
        </div>
      )}

      {/* Sticky anchor sub-nav — single page, smooth-scrolls to each section */}
      <div style={{ background: S.white, borderBottom: `1px solid ${S.hdrBorder}`, padding: '0 24px', display: 'flex', gap: '0', position: 'sticky', top: 0, zIndex: 20 }}>
        {[
          { id: 'summary-section', label: 'Summary' },
          { id: 'score-section', label: 'Score Summary' },
          { id: 'answerkey-section', label: 'Answer Key' },
        ].map(t => (
          <button key={t.id} onClick={() => document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            data-testid={`results-nav-${t.id}`} style={{
              padding: '12px 24px', fontSize: '14px', fontWeight: '500',
              color: S.textLight, borderBottom: '3px solid transparent',
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: S.font, transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = S.blue; }}
            onMouseLeave={e => { e.currentTarget.style.color = S.textLight; }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '24px' }}>

        {/* ═══ TEACHER REVIEW (shown once a teacher has left remarks or published) ═══ */}
        {(results.teacher_note?.text || results.teacher_reviewed_at || teacherCommentCount > 0) && (
          <div id="teacher-section" data-testid="teacher-review-banner" style={{ scrollMarginTop: '60px', background: 'linear-gradient(135deg,#fffbeb 0%,#fef3c7 100%)', border: '1px solid #fcd34d', borderRadius: '12px', padding: '18px 22px', marginBottom: '8px', boxShadow: '0 2px 10px rgba(245,158,11,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><GraduationCap size={20} /></div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: '#78350f' }}>Your teacher reviewed this test</div>
                  <div style={{ fontSize: '12px', color: '#92400e' }} data-testid="teacher-review-meta">
                    {results.teacher_reviewed_by?.name || results.teacher_note?.author_name || 'Your teacher'}
                    {results.teacher_reviewed_at ? ` · ${new Date(results.teacher_reviewed_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                    {teacherCommentCount > 0 ? ` · ${teacherCommentCount} remark${teacherCommentCount === 1 ? '' : 's'} on your answers` : ''}
                  </div>
                </div>
              </div>
              {teacherCommentCount > 0 && (
                <button onClick={() => document.getElementById('score-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} data-testid="teacher-review-jump" style={{ padding: '8px 16px', background: '#f59e0b', border: 'none', color: '#fff', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', borderRadius: '20px', fontFamily: S.font }}>
                  See remarks on my answers ↓
                </button>
              )}
            </div>
            {results.teacher_note?.text && (
              <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.7', color: '#1f2937', whiteSpace: 'pre-wrap', background: 'rgba(255,255,255,0.7)', borderRadius: '8px', padding: '12px 14px' }} data-testid="teacher-note-text">{results.teacher_note.text}</p>
            )}
          </div>
        )}

        {/* ═══ SUMMARY (top) ═══ */}
        <div id="summary-section" data-testid="results-summary-section" style={{ scrollMarginTop: '60px' }}>
          <div style={{ textAlign: 'center', padding: '40px 24px 24px' }}>
            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#d4edda', border: '4px solid #27ae60', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <span style={{ fontSize: '48px', color: '#27ae60' }}>&#10003;</span>
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: S.textDark, marginBottom: '8px' }}>Thank You!</h1>
            <p style={{ fontSize: '16px', color: S.textBody, marginBottom: '24px', maxWidth: '500px', margin: '0 auto 24px' }}>
              You have completed the {examName}. Your responses have been submitted.
            </p>

            {processingAI && (
              <div style={{ marginBottom: '24px' }} data-testid="ai-processing-banner">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '16px 28px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px', boxShadow: '0 4px 16px rgba(102,126,234,0.3)' }}>
                  <div style={{ width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.4)', borderTop: '3px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>AI is scoring your {processingLabel} responses...</span>
                </div>
                {waitedLong && (
                  <p style={{ fontSize: '13px', color: '#5a6b80', marginTop: '12px', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.55' }} data-testid="ai-processing-slow-note">
                    This is taking longer than usual — your recordings are saved. You can leave this page; the scores fill in automatically and will be here when you come back.
                  </p>
                )}
              </div>
            )}

            {!processingAI && (
              <p style={{ fontSize: '14px', color: '#27ae60', fontWeight: '600', marginBottom: '24px' }}>All scores are ready!</p>
            )}

            {results.status === 'completed' && (
              <div data-testid="teacher-feedback-cta"><FeedbackPrompt attemptId={attemptId} isPro={isPro} /></div>
            )}

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '16px', flexWrap: 'wrap' }}>
              <Link to={`/exams/${examId}`} data-testid="summary-retake-btn" style={{ padding: '12px 28px', background: '#27ae60', border: 'none', color: '#fff', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', borderRadius: '6px', fontFamily: S.font, textDecoration: 'none', display: 'inline-block' }}>
                Retake / Back to Exam
              </Link>
            </div>
          </div>
        </div>

        {/* ═══ SCORE SUMMARY ═══ */}
        <div id="score-section" data-testid="results-score-section" style={{ scrollMarginTop: '60px', marginTop: '8px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: S.textDark, margin: '16px 0', paddingBottom: '8px', borderBottom: `2px solid ${S.blue}` }}>Score Summary</h2>
          <div>
            {displaySkills.filter(sk => sk === 'listening' || sk === 'reading').map(sk => (
              <div key={sk} style={{ marginBottom: '24px' }}>
                <div style={{ background: S.hdrBg, padding: '12px 20px', borderBottom: `1px solid ${S.hdrBorder}` }}>
                  <span style={{ fontWeight: 'bold', fontSize: '15px', color: S.hdrText }}>Practice Test {testNum} - Your {SKILL_LABELS[sk]} CELPIP Score</span>
                </div>
                <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderTop: 'none', padding: '24px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                    <thead>
                      <tr style={{ background: '#3b4a5e', color: '#fff' }}>
                        <th colSpan={3} style={{ padding: '10px', textAlign: 'center', fontSize: '14px', fontWeight: 'bold' }}>CELPIP-GENERAL {SKILL_LABELS[sk]?.toUpperCase()} TEST</th>
                      </tr>
                      <tr style={{ background: '#f0f0f0' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #ccc' }}>Number of Questions</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #ccc' }}>Your Score</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #ccc' }}>Your Approximate CELPIP Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}>{skillScores[sk]?.total || 0}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}>{skillScores[sk]?.correct || 0}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold', color: S.blue }}>{skillScores[sk]?.clb || 'M'}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px', padding: '20px' }}>
                    <h4 style={{ textAlign: 'center', color: S.textDark, marginBottom: '10px', fontSize: '15px' }}>A Note About Your CELPIP Score</h4>
                    <p style={{ fontSize: '13px', color: S.textBody, lineHeight: '1.6' }}>
                      The score provided here is an estimate based on the {SKILL_LABELS[sk]} Test Score Conversion Chart. Each CELPIP test item is field tested and reviewed by an expert panel before it is integrated into the official test. Since questions may have different levels of difficulty and may therefore be equated differently, the raw score required for a certain level may vary slightly from one test to another.
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Writing & Speaking AI Scores */}
            {(writingResps.length > 0 || speakingResps.length > 0) && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ background: S.hdrBg, padding: '12px 20px', borderBottom: `1px solid ${S.hdrBorder}` }}>
                  <span style={{ fontWeight: 'bold', fontSize: '15px', color: S.hdrText }}>AI-Scored Sections</span>
                </div>
                <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderTop: 'none', padding: '24px' }}>
                  {/* iter63 — Combined overall CLB summary across all writing/speaking tasks */}
                  {(combinedWritingClb > 0 || combinedSpeakingClb > 0) && (
                    <div data-testid="combined-ai-clb-summary" style={{ display: 'grid', gridTemplateColumns: writingResps.length > 0 && speakingResps.length > 0 ? '1fr 1fr' : '1fr', gap: '14px', marginBottom: '20px' }}>
                      {writingResps.length > 0 && (
                        <div data-testid="combined-writing-clb-card" style={{ background: 'linear-gradient(135deg,#eef4ff 0%,#dbeafe 100%)', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '16px 18px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Overall Writing</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                            <span style={{ fontSize: '34px', fontWeight: 800, color: S.blue, lineHeight: 1 }}>{combinedWritingClb > 0 ? `CLB ${combinedWritingClb}` : '—'}</span>
                            <span style={{ fontSize: '12px', color: '#475569' }}>avg across {writingResps.length} task{writingResps.length === 1 ? '' : 's'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>Estimated — not an official CELPIP score.</div>
                        </div>
                      )}
                      {speakingResps.length > 0 && (
                        <div data-testid="combined-speaking-clb-card" style={{ background: 'linear-gradient(135deg,#fef3c7 0%,#fde68a 100%)', border: '1px solid #f59e0b', borderRadius: '10px', padding: '16px 18px' }}>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Overall Speaking</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                            <span style={{ fontSize: '34px', fontWeight: 800, color: '#b45309', lineHeight: 1 }}>{combinedSpeakingClb > 0 ? `Estimated CLB ${combinedSpeakingClb}` : '—'}</span>
                            <span style={{ fontSize: '12px', color: '#78350f' }}>avg across {speakingResps.length} task{speakingResps.length === 1 ? '' : 's'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#92400e', marginTop: '6px' }}>Estimated — not an official CELPIP score.</div>
                        </div>
                      )}
                    </div>
                  )}
                  {sortedWriting.map(({ r, label }) => <AIFeedbackCard key={`w-${r.id || r.question_id || label}`} response={r} label={label} />)}
                  {sortedSpeaking.map(({ r, label }) => <AIFeedbackCard key={`s-${r.id || r.question_id || label}`} response={r} label={label} />)}
                  {processingAI && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', marginTop: '12px' }}>
                      <div style={{ width: '24px', height: '24px', border: '3px solid #ffc107', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                      <span style={{ fontSize: '14px', color: '#8d6e08', fontWeight: '500' }}>AI is scoring your {processingLabel} responses... This may take a moment.</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══ ANSWER KEY ═══ */}
        <div id="answerkey-section" data-testid="results-answerkey-section" style={{ scrollMarginTop: '60px', marginTop: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: S.textDark, margin: '16px 0', paddingBottom: '8px', borderBottom: `2px solid ${S.blue}` }}>Answer Key</h2>
          <div>
            <div style={{ background: S.hdrBg, padding: '12px 20px', borderBottom: `1px solid ${S.hdrBorder}`, marginBottom: '0' }}>
              <span style={{ fontWeight: 'bold', fontSize: '15px', color: S.hdrText }}>Practice Test {testNum} - Result Page</span>
            </div>
            <div style={{ background: S.white, border: `1px solid ${S.hdrBorder}`, borderTop: 'none', padding: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: '600', color: S.textDark, borderBottom: '2px solid #ccc' }}>Question</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: '600', color: S.textDark, borderBottom: '2px solid #ccc' }}>Answer Key</th>
                    <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: '600', color: S.textDark, borderBottom: '2px solid #ccc' }}>Your Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAK.map((sec, si) => {
                    const rows = flattenRows(sec.questions);
                    if (rows.length === 0) return null;
                    return (
                      <React.Fragment key={si}>
                        {/* Section header */}
                        <tr><td colSpan={3} style={{ padding: '10px 12px', fontWeight: 'bold', color: S.textDark, fontSize: '13px', background: '#f0f2f5', borderBottom: '2px solid #ccc' }}>{sec.name}</td></tr>
                        {rows.map((row) => (
                          <React.Fragment key={row.key}>
                            <tr style={{ borderBottom: row.remark ? 'none' : '1px solid #e8e8e8', background: row.remark ? '#fffdf5' : undefined }}>
                              <td style={{ padding: '8px 12px', color: S.textBody }}>
                                {sec.name} - Q{row.index}
                                {row.remark && <span data-testid={`teacher-remark-pill-${row.qid}`} style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '1px 8px', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '10px', fontSize: '11px', fontWeight: '700', verticalAlign: 'middle' }}><GraduationCap size={11} /> Teacher remark</span>}
                              </td>
                              <td style={{ padding: '8px 12px', color: S.textBody }}>{row.correctText}</td>
                              <td style={{ padding: '8px 12px', color: row.isCorrect ? '#27ae60' : row.hasUserAnswer ? '#c0392b' : '#999', fontWeight: row.hasUserAnswer ? '500' : '400' }}>
                                {row.userText || '—'}
                                {row.hasUserAnswer && (row.isCorrect ? ' ✓' : ' ✗')}
                              </td>
                            </tr>
                            {row.remark && (
                              <tr style={{ borderBottom: '1px solid #e8e8e8', background: '#fffdf5' }}>
                                <td colSpan={3} style={{ padding: '0 12px 10px' }}>
                                  <TeacherRemark comment={row.remark} compact testId={`teacher-remark-${row.qid}`} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ─── AI Feedback Card — Enhanced with tips and improved version ── */
function AIFeedbackCard({ response, label }) {
  const teacherComment = response.teacher_comment && response.teacher_comment.text ? response.teacher_comment : null;
  const [open, setOpen] = useState(!!teacherComment);
  const [showImproved, setShowImproved] = useState(false);
  const fbRaw = response.ai_feedback || response.ai_score;
  const fb = (fbRaw && typeof fbRaw === 'object') ? fbRaw : null;
  const directScore = typeof response.ai_score === 'number' ? response.ai_score : 0;
  const scoreNum = (fb && typeof fb.overall_clb === 'number') ? fb.overall_clb : directScore;
  const scoreColor = scoreNum >= 9 ? '#27ae60' : scoreNum >= 7 ? '#2980b9' : scoreNum >= 5 ? '#f39c12' : '#e74c3c';

  // Speaking response → always show an audio player (or placeholder).
  const isSpeaking = response.response_type === 'audio_response';
  const audioUrl = resolveMediaUrl(response.audio_url || '');
  const rawTranscript = response.audio_transcript || response.text_response || '';
  const transcript = rawTranscript.trim().startsWith('(') ? '' : rawTranscript; // hide "(processing)" sentinels
  // iter147/148: answer exists but no score yet → "Processing…"; STT or scoring gave up → "Could not process" (answer kept).
  const hasAnswer = isSpeaking ? !!response.audio_url : !!(response.text_response && response.text_response.trim().length >= 10);
  const stillProcessing = scoreNum === 0 && hasAnswer && !fb;
  const transcriptionFailed = isSpeaking && !!fb?.transcription_failed;
  const scoringFailed = !!fb?.scoring_failed;
  const processingFailed = transcriptionFailed || scoringFailed;
  const silentRecording = isSpeaking && !!fb?.silent_recording;
  const kind = isSpeaking ? 'speaking' : 'writing';

  // iter62: Speaking scorer now returns estimated_level + confidence + score_range
  // + strengths + scoring_notes. Render them when present; writing path keeps
  // its existing (untouched) display.
  const estimatedLevel = isSpeaking && typeof fb?.estimated_level === 'number' ? fb.estimated_level : null;
  const scoreRange = isSpeaking && fb?.score_range ? fb.score_range : '';
  const confidence = isSpeaking && fb?.confidence ? fb.confidence : '';
  const strengths = Array.isArray(fb?.strengths) ? fb.strengths.filter(Boolean) : [];
  const scoringNotes = isSpeaking && fb?.scoring_notes ? fb.scoring_notes : '';
  const taskTypeLabel = isSpeaking && fb?.task_type ? fb.task_type : '';

  const confidenceColor = confidence === 'high' ? '#27ae60' : confidence === 'medium' ? '#f39c12' : '#999';

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '10px', marginBottom: '14px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: open ? '#f0f4ff' : '#fafbfc', border: 'none', cursor: 'pointer', fontFamily: S.font, textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '15px', fontWeight: 'bold', color: S.textDark }}>{label}</span>
          {scoreNum > 0 && (
            <span
              data-testid={`speaking-estimated-level-pill-${response.question_id || ''}`}
              style={{ padding: '3px 12px', background: scoreColor, color: '#fff', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' }}
            >
              {isSpeaking ? `Estimated CLB ${scoreNum}` : `CLB ${scoreNum}`}
            </span>
          )}
          {scoreNum === 0 && stillProcessing && <span style={{ padding: '3px 12px', background: '#dbeafe', color: '#1d4ed8', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' }} data-testid={`${kind}-processing-pill-${response.question_id || ''}`}>Processing…</span>}
          {scoreNum === 0 && !stillProcessing && processingFailed && <span style={{ padding: '3px 12px', background: '#fee2e2', color: '#b91c1c', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' }} data-testid={`${kind}-failed-pill-${response.question_id || ''}`}>{scoringFailed && !transcriptionFailed ? 'Could not score' : 'Could not process'}</span>}
          {scoreNum === 0 && !stillProcessing && !processingFailed && <span style={{ padding: '3px 12px', background: '#e0e0e0', color: '#888', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold' }}>{silentRecording ? 'No speech detected' : 'Not Scored'}</span>}
          {teacherComment && (
            <span data-testid={`teacher-remark-pill-${response.question_id || ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '10px', fontSize: '11px', fontWeight: '700' }}>
              <GraduationCap size={12} /> Teacher's remark
            </span>
          )}
          {isSpeaking && scoreRange && scoreNum > 0 && (
            <span
              data-testid={`speaking-score-range-${response.question_id || ''}`}
              style={{ padding: '2px 10px', background: '#eef4ff', color: '#2980b9', border: '1px solid #cfe2f5', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}
            >
              Range {scoreRange}
            </span>
          )}
          {isSpeaking && confidence && scoreNum > 0 && (
            <span
              data-testid={`speaking-confidence-${response.question_id || ''}`}
              style={{ padding: '2px 10px', background: '#fff', color: confidenceColor, border: `1px solid ${confidenceColor}`, borderRadius: '10px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' }}
              title="How sure the AI is about this estimated score"
            >
              {confidence} confidence
            </span>
          )}
        </div>
        {open ? <ChevronUp size={16} color="#999" /> : <ChevronDown size={16} color="#999" />}
      </button>
      {open && (
        <div style={{ borderTop: '1px solid #e8e8e8' }}>
          {teacherComment && (
            <div style={{ padding: '14px 20px 0' }}>
              <TeacherRemark comment={teacherComment} testId={`teacher-remark-${response.question_id || ''}`} />
            </div>
          )}
          {/* Estimated-level disclaimer + scoring notes (speaking only) */}
          {isSpeaking && scoreNum > 0 && (
            <div style={{ padding: '10px 20px', background: '#f5f8fc', borderBottom: '1px solid #eef2f7', fontSize: '11px', color: '#5a6b80', lineHeight: '1.6' }} data-testid="speaking-estimated-disclaimer">
              {taskTypeLabel && <div style={{ fontWeight: '700', color: S.textDark, marginBottom: '3px' }}>{taskTypeLabel}</div>}
              <em>This is an <strong>estimated</strong> CELPIP Speaking level — not an official score.</em>
              {scoringNotes && <div style={{ marginTop: '4px' }}>{scoringNotes}</div>}
            </div>
          )}

          {/* Audio playback (speaking only) */}
          {isSpeaking && (
            <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #eee' }}>
              {stillProcessing && (
                <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', color: '#1e40af', marginBottom: '12px', lineHeight: '1.55' }} data-testid={`speaking-processing-note-${response.question_id || ''}`}>
                  Your recording is saved. We are still turning it into text and scoring it — this can take a few minutes when many students finish at once. The score appears here automatically.
                </div>
              )}
              {processingFailed && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', color: '#991b1b', marginBottom: '12px', lineHeight: '1.55' }} data-testid={`speaking-failed-note-${response.question_id || ''}`}>
                  We couldn't {scoringFailed && !transcriptionFailed ? 'score' : 'process'} this recording after several attempts. Your audio is saved below — e-mail <strong>support@celpipmax.com</strong> with this test and we will re-run it for you.
                </div>
              )}
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: S.textDark, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Your Recording</div>
              {audioUrl ? (
                <AudioPlayer src={audioUrl} testId={`result-audio-${response.question_id || ''}`} />
              ) : (
                <div style={{ padding: '10px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '12px', color: '#b45309' }}>
                  Recording not available.
                </div>
              )}
              {transcript && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 'bold', color: S.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Transcript</div>
                  <div style={{ fontSize: '13px', color: S.textBody, lineHeight: '1.6', whiteSpace: 'pre-wrap', background: S.white, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>{transcript}</div>
                </div>
              )}
            </div>
          )}

          {/* Writing response text (writing only) */}
          {!isSpeaking && response.text_response && (
            <div style={{ padding: '14px 20px', background: '#f8fafc', borderBottom: '1px solid #eee' }}>
              {stillProcessing && (
                <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '12px', color: '#1e40af', marginBottom: '12px', lineHeight: '1.55' }} data-testid={`writing-processing-note-${response.question_id || ''}`}>
                  Your answer is saved. Our AI is still scoring it — this can take a few minutes when many students finish at once. The score appears here automatically.
                </div>
              )}
              {scoringFailed && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', color: '#991b1b', marginBottom: '12px', lineHeight: '1.55' }} data-testid={`writing-failed-note-${response.question_id || ''}`}>
                  We couldn't score this answer after several attempts. Your text is saved below — e-mail <strong>support@celpipmax.com</strong> with this test and we will re-run it for you.
                </div>
              )}              {response.selected_option && (
                <div style={{ marginBottom: '8px', display: 'inline-block', padding: '4px 12px', background: '#eef4ff', border: `1px solid ${S.blue}`, borderRadius: '14px', fontSize: '12px', fontWeight: 'bold', color: S.blue }} data-testid="result-writing-option">
                  Chose Option {response.selected_option}{response.selected_label ? `: ${response.selected_label}` : ''}
                </div>
              )}
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: S.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Your Response</div>
              <div style={{ fontSize: '13px', color: S.textBody, lineHeight: '1.6', whiteSpace: 'pre-wrap', background: S.white, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px', maxHeight: '220px', overflowY: 'auto' }}>{response.text_response}</div>
            </div>
          )}

          {/* Suggested Changes — inline corrections (writing only) */}
          {!isSpeaking && response.text_response && (
            <SuggestedChanges text={response.text_response} corrections={fb?.corrections} />
          )}

          {/* Criteria Scores */}
          {fb?.criteria && Object.keys(fb.criteria).length > 0 && (
            <div style={{ padding: '18px 20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {Object.entries(fb.criteria).map(([key, val]) => {
                const s = val?.score || 0;
                const sc = s >= 9 ? '#27ae60' : s >= 7 ? '#2980b9' : s >= 5 ? '#f39c12' : '#e74c3c';
                return (
                  <div key={key} style={{ background: '#f8f9fb', borderRadius: '8px', padding: '12px 14px', border: '1px solid #eee' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: S.textDark, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: sc }}>{s}<span style={{ fontSize: '10px', color: '#999', marginLeft: '2px' }}>/12</span></span>
                    </div>
                    <div style={{ fontSize: '12px', color: S.textBody, lineHeight: '1.5' }}>{val?.feedback || ''}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Strengths (speaking + writing) */}
          {strengths.length > 0 && (
            <div style={{ padding: '14px 20px', background: '#f0fdf4', borderTop: '1px solid #d1fae5' }} data-testid="speaking-strengths-block">
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#166534', marginBottom: '8px' }}>What You Did Well</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {strengths.map((st, i) => (
                  <div key={i} style={{ fontSize: '12px', color: '#14532d', paddingLeft: '18px', position: 'relative', lineHeight: '1.5' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#16a34a', fontWeight: 'bold' }}>✓</span> {st}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips to Improve */}
          {fb?.key_suggestions?.length > 0 && (
            <div style={{ padding: '14px 20px', background: '#fffbf0', borderTop: '1px solid #f0e8d0' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#8b6914', marginBottom: '8px' }}>Tips to Improve Your Score</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {fb.key_suggestions.map((s, i) => (
                  <div key={i} style={{ fontSize: '12px', color: '#6b5210', paddingLeft: '16px', position: 'relative', lineHeight: '1.5' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#d4a017', fontWeight: 'bold' }}>{i + 1}.</span> {s}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improved Version (CLB 10+) */}
          {fb?.improved_version && fb.improved_version !== '[AI-improved version would appear here]' && (
            <div style={{ padding: '14px 20px', borderTop: '1px solid #eee' }}>
              <button onClick={() => setShowImproved(!showImproved)} style={{ fontSize: '13px', fontWeight: 'bold', color: S.blue, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: S.font, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {showImproved ? '▾' : '▸'} View CLB 10+ Model Response
              </button>
              {showImproved && (
                <div style={{ marginTop: '10px', padding: '14px', background: '#f0f7ff', border: '1px solid #c8ddf0', borderRadius: '6px', fontSize: '13px', color: S.textBody, lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                  {fb.improved_version}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
